"use client";

import { useEffect, useRef, useState } from "react";
import { GOOGLE_MAPS_API_KEY, MAPS_PLACE_ID } from "@/lib/maps-config";

/**
 * Embedded Google map of the Ashland, KY shop.
 *
 * Uses the Maps Embed API (a plain <iframe>) rather than the Locator Plus
 * web component. The shop has one location, so the multi-location store
 * locator (search panel, results list, JS API) would be the wrong tool and
 * far heavier — this iframe pulls no JavaScript into our bundle.
 *
 * The iframe is mounted lazily via IntersectionObserver: it renders in the
 * footer on every page, so we don't want its network request (and Google's
 * cookies) firing on load. Until it's near the viewport we render a
 * same-sized placeholder so there's no layout shift when the real map swaps
 * in. `loading="lazy"` on the iframe is a second-line defense.
 *
 * Pass sizing via `className` (e.g. "aspect-video w-full").
 */
export default function StoreMap({ className = "" }: { className?: string }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;

    // No IntersectionObserver (very old browsers) — just render the map.
    // Deferred to a microtask so this isn't a synchronous setState in the
    // effect body (which cascades an extra render).
    if (typeof IntersectionObserver === "undefined") {
      const id = setTimeout(() => setInView(true), 0);
      return () => clearTimeout(id);
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const src =
    "https://www.google.com/maps/embed/v1/place" +
    `?key=${GOOGLE_MAPS_API_KEY}` +
    `&q=place_id:${MAPS_PLACE_ID}` +
    "&zoom=15";

  return (
    <div ref={containerRef} className={`block ${className}`}>
      {inView ? (
        <iframe
          title="Area Advertising, 824 Winchester Avenue, Ashland, KY"
          src={src}
          loading="lazy"
          allowFullScreen
          className="block h-full w-full"
        />
      ) : (
        <div
          aria-hidden="true"
          className="flex h-full w-full items-center justify-center bg-white/5 text-xs uppercase tracking-[0.2em] text-gray-500"
        >
          Loading map
        </div>
      )}
    </div>
  );
}
